import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Coredata, Service } from 'webapp-types';
import { Theme } from 'webapp/enums/enums';
import { LegacyMetricPopupType } from 'webapp/enums/metric-mover';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { addSeenSpaceId, parseSeenSpaceIds } from 'webapp/utils/legacy-metric-deletion';

type Value = string | number | boolean | null;
export type ConfirmPopupDontShowAgainValueKey = UserSettingsKey.EXIT_DESIGNER;

export default class UserSettingsService extends EmberService {
	@service('request') declare requestService: Service.Request;

	@tracked userSettings: Coredata.UserSettingsValues = {};

	get onboardingVendorSource(): string | null {
		return (this.userSettings[UserSettingsKey.VENDOR] as string) ?? null;
	}

	get templateId(): number | null {
		return (this.userSettings[UserSettingsKey.TEMPLATE_ID] as number) ?? null;
	}

	get showFirstWizardAfterCount(): number | null {
		return (this.userSettings[UserSettingsKey.SHOW_FIRST_WIZARD] as number) ?? null;
	}

	get showFirstWizard(): boolean {
		return this.showFirstWizardAfterCount === 0;
	}

	get skipYearlyUpgradeNotification(): number {
		return (this.userSettings[UserSettingsKey.SKIP_YEARLY_UPGRADE_NOTIFICATION] as number) ?? 0;
	}

	get cardExpiringPopupShownTimestamp(): number {
		return (this.userSettings[UserSettingsKey.CARD_EXPIRING_SHOWN_TIMESTAMP] as number) ?? 0;
	}

	get hasSeenBoardSchedulePopup(): boolean {
		return !!this.userSettings[UserSettingsKey.BOARD_SCHEDULE_POPUP_SEEN];
	}

	get hasSeenLegacyMetricsBanner(): boolean {
		return !!this.userSettings[UserSettingsKey.LEGACY_METRICS_BANNER_SEEN];
	}

	get hasDismissedUnverifiedAgencyWarning(): boolean {
		return !!this.userSettings[UserSettingsKey.UNVERIFIED_AGENCY_WARNING_DISMISSED];
	}

	set hasDismissedUnverifiedAgencyWarning(value: boolean) {
		void this.save(UserSettingsKey.UNVERIFIED_AGENCY_WARNING_DISMISSED, value);
	}

	get hasDismissedGenieConnectorsPromo(): boolean {
		return !!this.userSettings[UserSettingsKey.GENIE_CONNECTORS_PROMO_DISMISSED];
	}

	set hasDismissedGenieConnectorsPromo(value: boolean) {
		void this.save(UserSettingsKey.GENIE_CONNECTORS_PROMO_DISMISSED, value);
	}

	get hasSeenActivity(): boolean {
		return !!this.userSettings[UserSettingsKey.ACTIVITY_SEEN];
	}

	get showInvitedUserIntroVideo(): boolean {
		return !!this.userSettings[UserSettingsKey.SHOW_INVITED_USER_INTRO_VIDEO];
	}

	set showInvitedUserIntroVideo(value: boolean) {
		void this.save(UserSettingsKey.SHOW_INVITED_USER_INTRO_VIDEO, value);
	}

	get hasSeenAfterAutoTrialPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.AFTER_AUTO_TRIAL_POPUP_SEEN];
	}

	get hasSeenCustomMetricsGSheetsVideo(): boolean {
		return !!this.userSettings[UserSettingsKey.CUSTOM_METRICS_QB_GSHEETS_VIDEO_SEEN];
	}

	get lastSeenProductUpdates(): number {
		return (this.userSettings[UserSettingsKey.PRODUCT_UPDATES_SEEN_TIMESTAMP] as number) ?? 0;
	}

	get colorMode(): Theme {
		return (this.userSettings[UserSettingsKey.COLOR_MODE] as Theme) || Theme.LIGHT;
	}

	get isSignedUpFromTemplate(): boolean {
		return !!this.templateId; // persisent and available after finished onboarding
	}

	get hasSeenSelectedConnectionPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.SELECTED_CONNECTION_POPUP_SEEN];
	}

	get hasSeenLoopPromoPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.LOOP_PROMO_POPUP_SEEN];
	}

	get hasUsedAiSummaryBlock(): boolean {
		return !!this.userSettings[UserSettingsKey.AI_SUMMARY_BLOCK_USED];
	}

	get hasSeenGuidedOnboardingPopupStart(): boolean {
		return !!this.userSettings[UserSettingsKey.GUIDED_ONBOARDING_POPUP_START_SEEN];
	}

	get recentlyDeletedTourTimestamp(): number {
		return (
			(this.userSettings[UserSettingsKey.PROMOTION_RECENTLY_DELETED_TIMESTAMP] as number) ?? 0
		);
	}

	get inviteUserBoardPromoTimestamp(): number {
		return (
			(this.userSettings[
				UserSettingsKey.INVITE_USER_BOARD_PROMO_POPUP_SHOWN_TIMESTAMP
			] as number) ?? 0
		);
	}

	get inviteUserSourcePromoTimestamp(): number {
		return (
			(this.userSettings[
				UserSettingsKey.INVITE_USER_SOURCE_PROMO_POPUP_SHOWN_TIMESTAMP
			] as number) ?? 0
		);
	}

	get bestValuePlanBannerCooldownTimestamp(): number {
		return (
			(this.userSettings[
				UserSettingsKey.BEST_VALUE_PLAN_BANNER_COOLDOWN_TIMESTAMP
			] as number) ?? 0
		);
	}

	get hasSeenCustomMetricsPromoPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.CUSTOM_METRICS_PROMO_POPUP_SEEN];
	}

	get hasSeenGrowthTrialOfferPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.GROWTH_TRIAL_OFFER_POPUP_SEEN];
	}

	get clientPromoBannerClosedTimestamp(): number {
		return (
			(this.userSettings[UserSettingsKey.CLIENT_PROMO_BANNER_CLOSED_TIMESTAMP] as number) ?? 0
		);
	}

	get hasSeenTrialExpiredPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.TRIAL_EXPIRED_POPUP_SEEN];
	}

	get isOkrOverviewHidden(): boolean {
		return !!this.userSettings[UserSettingsKey.OKR_OVERVIEW_HIDDEN];
	}

	get hasSeeOnboardingWelcome(): boolean {
		return !!this.userSettings[UserSettingsKey.ONBOARDING_WELCOME_SEEN];
	}

	get hasSeenAiSummaryAutocreatePopup(): boolean {
		return !!this.userSettings[UserSettingsKey.AI_SUMMARY_AUTOCREATE_POPUP_SEEN];
	}

	set hasSeenAiSummaryAutocreatePopup(value: boolean) {
		void this.save(UserSettingsKey.AI_SUMMARY_AUTOCREATE_POPUP_SEEN, value);
	}

	get isHomeAttributionQuestionClosed(): boolean {
		return !!this.userSettings[UserSettingsKey.HOME_ATTRIBUTION_QUESTION_CLOSED];
	}

	set isHomeAttributionQuestionClosed(value: boolean) {
		void this.save(UserSettingsKey.HOME_ATTRIBUTION_QUESTION_CLOSED, value);
	}

	get hasSeenOnboardingGeniePopup(): boolean {
		return !!this.userSettings[UserSettingsKey.ONBOARDING_GENIE_POPUP_SEEN];
	}

	set hasSeenOnboardingGeniePopup(value: boolean) {
		void this.save(UserSettingsKey.ONBOARDING_GENIE_POPUP_SEEN, value);
	}

	get hasSeenAgenticIntroPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.AGENTIC_INTRO_POPUP_SEEN];
	}

	set hasSeenAgenticIntroPopup(value: boolean) {
		void this.save(UserSettingsKey.AGENTIC_INTRO_POPUP_SEEN, value);
	}

	get hasSeenOnboardingGenieArtifactsPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.ONBOARDING_GENIE_ARTIFACTS_POPUP_SEEN];
	}

	set hasSeenOnboardingGenieArtifactsPopup(value: boolean) {
		void this.save(UserSettingsKey.ONBOARDING_GENIE_ARTIFACTS_POPUP_SEEN, value);
	}

	get hasSeenOnboardingNewNavigationPopup(): boolean {
		return !!this.userSettings[UserSettingsKey.ONBOARDING_NEW_NAVIGATION_POPUP_SEEN];
	}

	set hasSeenOnboardingNewNavigationPopup(value: boolean) {
		void this.save(UserSettingsKey.ONBOARDING_NEW_NAVIGATION_POPUP_SEEN, value);
	}

	get hasSeenOnboardingChatLeaveConfirm(): boolean {
		return !!this.userSettings[UserSettingsKey.ONBOARDING_CHAT_LEAVE_CONFIRM_SEEN];
	}

	set hasSeenOnboardingChatLeaveConfirm(value: boolean) {
		void this.save(UserSettingsKey.ONBOARDING_CHAT_LEAVE_CONFIRM_SEEN, value);
	}

	// The onboarding chat the Genie icon reopens; see navigation-main resumeThreadId.
	get onboardingChatThreadId(): string | null {
		return (this.userSettings[UserSettingsKey.ONBOARDING_CHAT_THREAD_ID] as string) || null;
	}

	set onboardingChatThreadId(value: string | null) {
		if (!value) {
			void this.delete(UserSettingsKey.ONBOARDING_CHAT_THREAD_ID);
			return;
		}

		void this.save(UserSettingsKey.ONBOARDING_CHAT_THREAD_ID, value);
	}

	get preferMainNavigationExpanded(): boolean {
		return !this.userSettings[UserSettingsKey.MAIN_NAVIGATION_COLLAPSED];
	}

	set preferMainNavigationExpanded(value: boolean) {
		void this.save(UserSettingsKey.MAIN_NAVIGATION_COLLAPSED, !value);
	}

	// The legacy metric deletion popups are seen once per account: each key holds the
	// comma-separated ids of the spaces (accounts) where the user has seen it.
	hasSeenLegacyMetricPopup(type: LegacyMetricPopupType, spaceId: number): boolean {
		const value = this.userSettings[this.getLegacyMetricPopupSeenKey(type)];
		return parseSeenSpaceIds(value).includes(spaceId);
	}

	markLegacyMetricPopupSeen(type: LegacyMetricPopupType, spaceId: number): void {
		if (this.hasSeenLegacyMetricPopup(type, spaceId)) {
			return;
		}

		const key = this.getLegacyMetricPopupSeenKey(type);
		void this.save(key, addSeenSpaceId(this.userSettings[key], spaceId));
	}

	loadUserSettings(data: Coredata.UserSettingsValues) {
		this.userSettings = data;
	}

	updateFirstWizardEligibility() {
		let showFirstWizardAfter = this.showFirstWizardAfterCount;

		if (showFirstWizardAfter !== null) {
			if (showFirstWizardAfter > 0) {
				showFirstWizardAfter--;
				void this.save(UserSettingsKey.SHOW_FIRST_WIZARD, showFirstWizardAfter);
			}
		}
	}

	saveLocal(key: UserSettingsKey, value: Value) {
		this.userSettings[key] = value;
		this.userSettings = this.userSettings;
	}

	save(key: UserSettingsKey, value: Value, isTour?: boolean) {
		this.saveLocal(key, value);

		if (typeof value === 'boolean') {
			value = +value;
		}

		return this.doRequest('POST', key, isTour, value);
	}

	delete(key: UserSettingsKey, isTour?: boolean) {
		delete this.userSettings[key];
		this.userSettings = this.userSettings;
		return this.doRequest('DELETE', key, isTour);
	}

	// extra methods for confirm popup dont show again where we force boolean value

	saveConfirmPopupDontShowAgainValue(key: ConfirmPopupDontShowAgainValueKey, value: boolean) {
		void this.save(key, value);
	}

	getConfirmPopupDontShowAgainValue(key: ConfirmPopupDontShowAgainValueKey): boolean {
		const value = this.userSettings[key];
		return typeof value === 'boolean' ? value : false;
	}

	private getLegacyMetricPopupSeenKey(type: LegacyMetricPopupType): UserSettingsKey {
		return type === LegacyMetricPopupType.PUSH_API
			? UserSettingsKey.METRIC_MOVER_PUSHAPI_POPUP_SEEN_SPACES
			: UserSettingsKey.METRIC_MOVER_POPUP_SEEN_SPACES;
	}

	private doRequest(method: string, key: UserSettingsKey, isTour = false, value?: Value) {
		const propertyKey = isTour ? `tour_${key}_done` : key,
			data = typeof value === 'undefined' ? null : { value };
		return this.requestService
			.api(method, `/user-settings/${propertyKey}`, data)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}
}

declare module '@ember/service' {
	interface Registry {
		'user-settings': UserSettingsService;
	}
}
